import React, { useRef, useEffect } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Eraser } from 'lucide-react';

/**
 * Wordのような簡易リッチテキストエディタ（contentEditable + execCommand）
 * value: HTML文字列 / onChange: (html) => void
 */
export const RichTextEditor = ({ value = '', onChange, placeholder = '', testId = 'rich-editor' }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const emit = () => {
    if (ref.current && onChange) onChange(ref.current.innerHTML);
  };

  const exec = (command, arg = null) => {
    document.execCommand('styleWithCSS', false, true);
    document.execCommand(command, false, arg);
    ref.current && ref.current.focus();
    emit();
  };

  const btn = 'h-8 w-8 flex items-center justify-center rounded border border-amber-500/30 text-gray-700 hover:bg-amber-500/10 transition-colors';

  return (
    <div className="rounded-lg border border-amber-500/30 bg-white/70 overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-amber-500/20 bg-amber-50/50">
        <button type="button" className={btn} onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} title="太字" data-testid={`${testId}-bold`}><Bold className="w-4 h-4" /></button>
        <button type="button" className={btn} onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} title="斜体" data-testid={`${testId}-italic`}><Italic className="w-4 h-4" /></button>
        <button type="button" className={btn} onMouseDown={(e) => { e.preventDefault(); exec('underline'); }} title="下線" data-testid={`${testId}-underline`}><Underline className="w-4 h-4" /></button>
        <span className="w-px h-6 bg-amber-500/20 mx-1" />
        <select
          className="h-8 px-1 rounded border border-amber-500/30 text-sm text-gray-700 bg-white"
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { exec('fontSize', e.target.value); e.target.selectedIndex = 0; }}
          title="文字サイズ"
          data-testid={`${testId}-size`}
          defaultValue=""
        >
          <option value="" disabled>サイズ</option>
          <option value="2">小</option>
          <option value="3">標準</option>
          <option value="5">大</option>
          <option value="6">特大</option>
        </select>
        <span className="w-px h-6 bg-amber-500/20 mx-1" />
        <button type="button" className={`${btn} text-black`} onMouseDown={(e) => { e.preventDefault(); exec('foreColor', '#111827'); }} title="黒" data-testid={`${testId}-color-black`}><span className="w-4 h-4 rounded-full bg-gray-900 border border-gray-400" /></button>
        <button type="button" className={btn} onMouseDown={(e) => { e.preventDefault(); exec('foreColor', '#C8A24B'); }} title="金" data-testid={`${testId}-color-gold`}><span className="w-4 h-4 rounded-full" style={{ backgroundColor: '#C8A24B' }} /></button>
        <button type="button" className={btn} onMouseDown={(e) => { e.preventDefault(); exec('foreColor', '#6B7280'); }} title="グレー" data-testid={`${testId}-color-gray`}><span className="w-4 h-4 rounded-full bg-gray-500" /></button>
        <span className="w-px h-6 bg-amber-500/20 mx-1" />
        <button type="button" className={btn} onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }} title="箇条書き" data-testid={`${testId}-ul`}><List className="w-4 h-4" /></button>
        <button type="button" className={btn} onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }} title="番号付き" data-testid={`${testId}-ol`}><ListOrdered className="w-4 h-4" /></button>
        <span className="w-px h-6 bg-amber-500/20 mx-1" />
        <button type="button" className={btn} onMouseDown={(e) => { e.preventDefault(); exec('justifyLeft'); }} title="左寄せ" data-testid={`${testId}-left`}><AlignLeft className="w-4 h-4" /></button>
        <button type="button" className={btn} onMouseDown={(e) => { e.preventDefault(); exec('justifyCenter'); }} title="中央" data-testid={`${testId}-center`}><AlignCenter className="w-4 h-4" /></button>
        <button type="button" className={btn} onMouseDown={(e) => { e.preventDefault(); exec('justifyRight'); }} title="右寄せ" data-testid={`${testId}-right`}><AlignRight className="w-4 h-4" /></button>
        <span className="w-px h-6 bg-amber-500/20 mx-1" />
        <button type="button" className={btn} onMouseDown={(e) => { e.preventDefault(); exec('removeFormat'); }} title="書式クリア" data-testid={`${testId}-clear`}><Eraser className="w-4 h-4" /></button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder}
        className="rich-content min-h-[120px] px-4 py-3 text-gray-900 text-sm focus:outline-none"
        data-testid={`${testId}-area`}
      />
    </div>
  );
};

export default RichTextEditor;
